import styled from "styled-components";

const withAlpha = (color, alpha = "22") => {
  if (!color || typeof color !== "string") {
    return `#2563eb${alpha}`;
  }

  const trimmed = color.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) {
    return `${trimmed}${alpha}`;
  }

  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    const [, r, g, b] = trimmed;
    return `#${r}${r}${g}${g}${b}${b}${alpha}`;
  }

  return trimmed;
};

export const StyledLineItemCard = styled.div`
  width: 100%;
  background: var(--rms-v2-surface);
  border: 1px solid var(--rms-v2-border);
  box-shadow: none;
  padding: 14px;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  overflow: visible;
  position: relative;
  border-radius: ${(props) => (props.rounded ? "10px" : "0px")};

  &.ineligible {
    cursor: not-allowed;
  }

  &.request-created {
    cursor: default;
  }

  &.ineligible::after {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.62);
    border-radius: inherit;
    pointer-events: none;
    z-index: 2;
  }

  &.selected {
    border-color: ${(props) => props.primaryColor}44;
    box-shadow: 0 0 0 1px ${(props) => props.primaryColor};
    overflow: visible !important;
  }

  &.error-highlighted {
    border-color: var(--rms-v2-danger);
    box-shadow: 0 0 0 1px var(--rms-v2-danger);
  }

  .line-item-card-main {
    display: grid;
    grid-template-columns: 64px 1fr 28px;
    gap: 14px;
    align-items: start;
    position: relative;
    margin-top: 0;
  }

  &.configured .line-item-card-main {
    margin-top: 10px;
  }

  &.request-created .line-item-image,
  &.request-created .line-item-details,
  &.request-created .line-item-price-summary {
    opacity: 0.6;
  }

  .line-item-image {
    width: 64px;
    height: 64px;
    display: block;
    border-radius: ${(props) => (props.rounded ? "10px" : "0px")};
    object-fit: cover;
    background: var(--rms-v2-surface-muted);
  }

  .line-item-details {
    min-width: 0;
    padding-top: 4px;
  }

  .line-item-title {
    margin: 0;
    color: var(--rms-v2-text-muted);
    font-size: 14px;
    font-weight: 900;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    max-width: 100%;
    text-overflow: ellipsis;
  }

  .line-item-variant {
    margin: 6px 0 0;
    color: var(--rms-v2-text-muted);
    font-size: 10px;
    font-weight: 600;
  }

  .line-item-meta-row {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: start;
    gap: 10px;
    margin-top: 5px;
  }

  .line-item-quantity {
    margin: 0;
    color: var(--rms-v2-text-muted);
    font-size: 12px;
    font-weight: 600;
  }

  .line-item-price-row {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    min-width: 0;
    position: relative;
  }

  .line-item-price {
    margin: 0;
    font-size: 14px;
    font-weight: 900;
    color: var(--rms-v2-text-title) !important;
    text-align: right;
    white-space: nowrap;
  }

  .line-item-price-stack {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: 3px;
  }

  .line-item-current-price,
  .line-item-compare-price {
    display: inline-block;
  }

  .line-item-compare-price {
    margin-left: 0 !important;
    color: var(--rms-v2-text-muted);
    font-size: 13px;
    font-weight: 500;
    text-decoration: line-through;
  }

  .line-item-ineligible-messages {
    position: relative;
    z-index: 3;
    margin-top: 8px;
  }

  .line-item-ineligible-messages p {
    margin: 4px 0 0;
    color: var(--rms-v2-text-muted);
    font-size: 12px;
    font-weight: 600;
  }

  .line-item-questionnaire-review {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--rms-v2-border);
  }

  .line-item-questionnaire-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px;
    align-items: start;
  }

  .line-item-questionnaire-row span {
    color: var(--rms-v2-text-muted);
    font-size: 11px;
    font-weight: 700;
    line-height: 1.35;
  }

  .line-item-questionnaire-row p {
    margin: 0;
    color: var(--rms-v2-text-title);
    font-size: 11px;
    font-weight: 900;
    line-height: 1.35;
    text-align: right;
    max-width: 110px;
    overflow-wrap: anywhere;
  }

  .line-item-side-summary {
    min-height: 64px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    padding-top: 4px;
  }

  .line-item-selection-summary {
    justify-content: space-between;
  }

  .line-item-price-summary {
    justify-content: flex-end;
  }

  .line-item-side-summary .line-item-price {
    line-height: 1;
  }

  .configured-corner-tag {
    display: inline-flex;
    align-items: center;
    position: absolute;
    top: -24px;
    right: -14px;
    padding: 6px 10px;
    border-radius: 0 ${(props) => (props.rounded ? "10px" : "0px")};
    background: ${(props) => withAlpha(props.primaryColor || "#2563eb", "22")};
    color: ${(props) => props.primaryColor};
    font-size: 11px;
    font-weight: 800;
    white-space: nowrap;
    z-index: 99;
  }

  .configured-edit-button {
    border: 0;
    background: transparent;
    color: ${(props) => props.primaryColor};
    font-size: 14px;
    font-weight: 800;
    cursor: pointer;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .configured-edit-button svg {
    width: 17px;
    height: 17px;
  }

  .configured-card-actions {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
  }

  .configured-delete-button {
    width: 28px;
    height: 28px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: var(--rms-v2-text-muted);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
  }

  .configured-delete-button svg {
    width: 17px;
    height: 17px;
  }

  .track-request-action {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid var(--rms-v2-border-soft);
  }

  .track-request-button {
    width: min(200px, 100%);
    height: 40px;
    min-height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 16px;
    border: 1px solid ${(props) => props.primaryColor};
    border-radius: ${(props) => (props.rounded ? "10px" : "0px")};
    background: ${(props) => props.primaryColor};
    color: var(--rms-v2-surface);
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
  }

  .track-request-button svg {
    width: 16px;
    height: 16px;
  }

  .line-item-actions {
    margin-top: 12px;
    overflow: visible;
    transform-origin: top;
  }

  .replacement-actions .replacement-summary {
    margin-top: 0;
  }

  .line-item-actions-enter {
    animation: v2ActionSlideDown 220ms ease-out;
  }

  @keyframes v2ActionSlideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .replacement-summary {
    margin-top: 12px;
    border-top: 1px solid var(--rms-v2-border-soft);
    padding-top: 10px;
  }

  .replacement-summary-label {
    color: var(--rms-v2-text-muted);
    font-size: 12px;
    font-weight: 700;
    margin: 0 0 8px;
  }

  .replacement-summary-panel {
    align-items: center;
    background: var(--rms-v2-primary-faint);
    border: 1px solid var(--rms-v2-primary-soft);
    border-radius: ${(props) => (props.rounded ? "10px" : "0px")};
    color: inherit;
    cursor: pointer;
    display: grid;
    gap: 10px;
    grid-template-columns: 38px minmax(0, 1fr) 18px;
    font: inherit;
    margin: 0;
    padding: 8px 10px;
    text-align: left;
    width: 100%;
    transition:
      background 180ms ease,
      border-color 180ms ease,
      grid-template-columns 220ms ease,
      padding 220ms ease;
  }

  .replacement-summary-panel.open {
    align-items: flex-start;
    grid-template-columns: 54px minmax(0, 1fr) 18px;
    padding: 10px;
  }

  .replacement-summary-panel svg {
    color: ${(props) => props.primaryColor};
    height: 18px;
    transition: transform 180ms ease;
    width: 18px;
  }

  .replacement-summary-panel svg.open {
    transform: rotate(180deg);
  }

  .replacement-summary-image {
    width: 38px;
    height: 38px;
    border-radius: ${(props) => (props.rounded ? "10px" : "0px")};
    object-fit: cover;
    background: var(--rms-v2-surface);
    transition:
      width 220ms ease,
      height 220ms ease,
      background 220ms ease;
  }

  .replacement-summary-panel.open .replacement-summary-image {
    background: var(--rms-v2-surface-muted);
    height: 54px;
    width: 54px;
  }

  .replacement-summary-copy {
    min-width: 0;
  }

  .replacement-summary-title {
    color: var(--rms-v2-text-title);
    font-size: 13px;
    font-weight: 700;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: font-weight 180ms ease;
  }

  .replacement-summary-panel.open .replacement-summary-title {
    display: -webkit-box;
    font-weight: 800;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: normal;
  }

  .replacement-summary-expanded-copy {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    transform: translateY(-6px);
    transition:
      max-height 220ms ease,
      opacity 180ms ease,
      transform 220ms ease;
  }

  .replacement-summary-panel.open .replacement-summary-expanded-copy {
    max-height: 90px;
    opacity: 1;
    transform: translateY(0);
  }

  .replacement-detail-meta {
    color: var(--rms-v2-text-muted);
    font-size: 12px;
    font-weight: 600;
    margin-top: 4px !important;
  }

  .replacement-detail-price {
    color: var(--rms-v2-text-title);
    font-size: 14px;
    font-weight: 900;
    margin-top: 6px !important;
  }
`;
